import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { InboxOutlined } from "@ant-design/icons";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, MaximizeIcon, RestoreIcon } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2022_R01Store } from "../Store_KSK2022_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultKSK2022_R01DetailDto, KSK2022_R01DetailDto, KSK2022_R01InspectDto } from "../Dto_KSK2022_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useDescText } from "src/hooks/useDescText";
import { DESC_BTN, DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";

const COLUMN_STATE_KEY = 'KSK2022_R01-detail-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['warehouseNm', 'childItemcd', 'itemNm', 'itemSpec', 'lotNo', 'lotQty', 'bomUsingQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: [''],
	
	pointerCells: ['transportQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	
	alignLeftCells: ['']
};

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2022_R01DetailDto> | null>;
	defaultKSK2022_R01DetailRow: KSK2022_R01DetailDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const DetailTable_KSK2022_R01 = (props: Props) => {

	const { gridRef, defaultKSK2022_R01DetailRow, isMaximized = false, onToggleMaximize } = props;

	const { 
			KSK2022_R01DetailList, setKSK2022_R01DetailList, 
			editKSK2022_R01,
			selectedKSK2022_R01DetailList, setSelectedKSK2022_R01DetailList, removeSelectedKSK2022_R01DetailList, addSelectedKSK2022_R01DetailList, initSelectedKSK2022_R01DetailList,
			deletedKSK2022_R01DetailList, setDeletedKSK2022_R01DetailList, addDeletedKSK2022_R01DetailList, removeDeletedKSK2022_R01DetailList,
			insertKSK2022_R01DetailList, updateKSK2022_R01DetailList, removeKSK2022_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalKSK2022_R01DetailList, setOriginalKSK2022_R01DetailList,
		} = useKSK2022_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const { renderDescText } = useDescText();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2022_R01DetailList,
		defaultGridData: defaultKSK2022_R01DetailRow,
		selectedRow: selectedKSK2022_R01DetailList,
		originalGridData: originalKSK2022_R01DetailList,
		updateRow: (index: number, data: any) => updateKSK2022_R01DetailList(index, data),
	});

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		warehouseNm: renderDescText(DESC_FLD.OUTBOUND_WAREHOUSE, "출고창고"),
		itemCd: renderDescText(DESC_FLD.MATERIAL_CODE, "자재코드"),
		itemNm: renderDescText(DESC_FLD.ITEM_NAME, "품명"),
		itemSpec: renderDescText(DESC_FLD.SPECIFICATION, "규격"),
		lotNo: renderDescText(DESC_FLD.LOT_NO, "Lot No"),
		lotQty: renderDescText(DESC_FLD.INVENTORY_QTY, "재고량"),
		bomUsingQty: renderDescText(DESC_FLD.REQUIRED_QTY, "소요량"),
		transportQty: renderDescText(DESC_FLD.OUTBOUND_QTY, "출고량"),
	}), [renderDescText]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<KSK2022_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedKSK2022_R01DetailList,
            deletedList: deletedKSK2022_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedKSK2022_R01DetailList, deletedKSK2022_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);
	const baseColumnDefs: ColDef<KSK2022_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
		),
		{
			headerName: renderDescText(DESC_FLD.OUTBOUND_WAREHOUSE, "출고창고"), colId: 'warehouseNm', field: 'warehouseNm', width: 90,
			editable: false,
		},
        {
			headerName: renderDescText(DESC_FLD.MATERIAL_CODE, "자재코드"), colId: 'childItemcd', field: 'childItemcd', width: 150,
			editable: false,
		},
		{
			headerName: renderDescText(DESC_FLD.ITEM_NAME, "품명"), colId: 'itemNm', field: 'itemNm', width: 350,
			editable: false,
		},
		{
			headerName: renderDescText(DESC_FLD.SPECIFICATION, "규격"), colId: 'itemSpec', field: 'itemSpec', width: 200,
			editable: false,
		},
		{
			headerName: renderDescText(DESC_FLD.LOT_NO, "Lot No"), colId: 'lotNo', field: 'lotNo', width: 140,
			editable: false,
		},
		{
			headerName: renderDescText(DESC_FLD.INVENTORY_QTY, "재고량"), colId: 'lotQty', field: 'lotQty', width: 90,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
        //		headerName: '개당소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 90,
		//		editable: false,
		// },
		// {
        //		headerName: '손실률', colId: 'lossPercent', field: 'lossPercent', width: 90,
		//		editable: false,
		// },
		{
			headerName: renderDescText(DESC_FLD.REQUIRED_QTY, "소요량"), colId: 'bomUsingQty', field: 'bomUsingQty', width: 90,
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// {
        //		headerName: '출고완료량', colId: 'transportedQty', field: 'transportedQty', width: 65,
		//		editable: false,
		// },
		{
			headerName: renderDescText(DESC_FLD.OUTBOUND_QTY, "출고량"), colId: 'transportQty', field: 'transportQty', width: 90,
			editable: true,
			pasteable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
		},
		// ...IndexParameterCell(),
	], [deletedKSK2022_R01DetailList, KSK2022_R01DetailList, renderDescText]);

	const columnDefs: ColDef<KSK2022_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2022_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: KSK2022_R01DetailList,
		originalDataList: originalKSK2022_R01DetailList,
		updateRow: updateKSK2022_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'transportQty') {
				if(updatedData.transportQty === 0) {
					updatedData.isModified = false;
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2022_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedKSK2022_R01DetailList,
		deletedList: deletedKSK2022_R01DetailList,
		addSelected: addSelectedKSK2022_R01DetailList,
		removeSelected: removeSelectedKSK2022_R01DetailList,
		clickable, setClickable
	});

	const commonInputColumns = ['transportQty', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['transportQty', ];


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2022_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2022_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2022_R01DetailList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2022_R01DetailDto>[] = useMemo(() => [
	], [editKSK2022_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2022_R01DetailList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2022_R01DetailDto>) => {
		postSortHandler(params, KSK2022_R01DetailList, originalKSK2022_R01DetailList, setKSK2022_R01DetailList);
	}, [KSK2022_R01DetailList, originalKSK2022_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			warehouseNm: formatDescCount(
				KSK2022_R01DetailList.length,
				renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건"),
			),
			// lotQty: KSK2022_R01DetailList.reduce((sum, item) => sum + (deletedKSK2022_R01DetailList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [KSK2022_R01DetailList, deletedKSK2022_R01DetailList, renderDescText]);

	return (
		<>
			<div className="flex h-full min-h-full flex-1 overflow-hidden rounded-[12px] border border-[#d3e0f0] bg-white shadow-sm">
				<GridContextWrapper<KSK2022_R01DetailDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={undefined}
					// setPaddedGridData={setKSK2022_R01DetailList}
					initSelected={initSelectedKSK2022_R01DetailList}
					defaultRowData={defaultKSK2022_R01DetailRow}
					baseColumnDefs={columnDefs}	
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<div className="flex h-[36px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[16px] text-[17px] font-bold text-white">
						<InboxOutlined />
						<span>{renderDescText(DESC_FLD.PROCESS_INPUT_CONSUMED_MATERIAL_LIST, "공정투입 소비자재 목록")}</span>
						<button
							type="button"
							className="ml-auto flex h-[30px] w-[34px] items-center justify-center rounded-[6px] border border-white/40 bg-white/10 hover:bg-white/20"
							onClick={onToggleMaximize}
							title={isMaximized
								? renderDescText(DESC_BTN.MINIMIZE, "최소화")
								: renderDescText(DESC_BTN.MAXIMIZE, "최대화")}
						>
							{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
						</button>
					</div>
					<Grid_Excel<KSK2022_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}	getRowId={getRowId}
						columnDefs={columnDefs}		
						defaultColDef={gridDefaultColDef}
						overlayNoRowsTemplate={`<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`}

						rowClassRules={createRowClassRules<KSK2022_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedKSK2022_R01DetailList,
							deletedList: deletedKSK2022_R01DetailList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setDetailColumnState}		//컬럼 상태 저장
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}
